import { Banner } from '@workspace/ui/components/banner'
import type { ReactNode } from 'react'
import type { BannerKey } from '../config/copy'
import { BANNER_COPY } from '../config/copy'
import type { CopyTokens } from '../lib/interpolate'
import { interpolate } from '../lib/interpolate'

export interface HomeBannersProps {
  banners: BannerKey[]
  tokens: CopyTokens
  /** Destination per banner. A banner with no action is not shown. */
  onAction: (banner: BannerKey) => void
}

const TONE: Record<BannerKey, 'warning' | 'success'> = {
  BANNER_TRIAL_ENDING: 'warning',
  BANNER_LOW_RUNWAY: 'warning',
  // Green, not blue: an investor has replied, which is the thing the whole
  // product is for. Blue reads as a system notice — something the app wants to
  // tell you about itself — and buries the best news on the page in it.
  BANNER_REPLIES_WAITING: 'success',
}

/**
 * Banners ride under whichever card won.
 *
 * They are resolved server-side after the focus key is known, so a reply that
 * lost priority to a lost sender still surfaces here rather than vanishing
 * from every surface.
 */
/** Uppercase the first letter only — the rest of the line is already written. */
const sentenceCase = (text: string) => text.charAt(0).toUpperCase() + text.slice(1)

export function HomeBanners({ banners, tokens, onAction }: HomeBannersProps) {
  if (banners.length === 0) return null

  return (
    <div className="flex flex-col gap-2">
      {banners.map((banner) => {
        const copy = BANNER_COPY[banner]
        const message: ReactNode = (
          <span className="text-[13px] font-normal">
            {/* The text opens with a token, and a token like "less than a day"
                does not know it is starting a sentence. */}
            {sentenceCase(interpolate(copy.text, tokens))}{' '}
            <button
              type="button"
              onClick={() => onAction(banner)}
              className="font-medium text-foreground underline underline-offset-2 hover:text-primary"
            >
              {copy.linkLabel}
            </button>
            {copy.suffix ? ` ${copy.suffix}` : ''}
          </span>
        )
        // The sentence IS the banner — there is no separate heading to add
        // above it, so it goes in `title` rather than inventing one.
        return <Banner key={banner} variant={TONE[banner]} title={message as never} />
      })}
    </div>
  )
}
